import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import client from '@api';
import { unreadThreadsCountQuery } from '@graphql/queries/threads';
import { threadEventsSubscription } from '@graphql/subscription/threads';
import { useWsReconnect } from '@hooks/useWsReconnect';
import { SILENT_CONTEXT } from '@modules/Workspace/campaigns/schemaGap';
import { ErrorCategory, handleError } from '@services/errorService';

import type { RootState } from '@app-types/index';

import useInboxAvailable from './hooks/useInboxAvailable';
import type { Thread, ThreadEvent } from './types';
import {
  EMPTY_UNREAD,
  type UnreadState,
  type UnreadStep,
  applyEventToUnread,
  seedUnread,
  setThreadUnread,
} from './unread';

export interface ThreadEventsListener {
  onEvent: (event: ThreadEvent) => void;
  /** WS reconnect or RESYNC_REQUIRED: events may have been missed — refetch. */
  onResync: () => void;
}

export interface ThreadEventsHub {
  /** False when the feature isn't available for this org. */
  active: boolean;
  addListener: (listener: ThreadEventsListener) => () => void;
  /** A read state we learned first-hand (e.g. the `threadMarkRead` result). */
  applyReadState: (threadId: string, unreadCount: number, seq?: number) => void;
  /** Teach the badge about threads from a list snapshot. */
  seed: (threads: Pick<Thread, '_id' | 'unreadCount' | 'seq'>[]) => void;
}

const HubContext = createContext<ThreadEventsHub | null>(null);
const UnreadContext = createContext<number>(0);

/** Coalesces the refetches a burst of ambiguous events asks for. */
const REFETCH_DEBOUNCE_MS = 300;

/** RESYNC_REQUIRED ends the stream while the socket stays up. */
export const isResyncRequired = (err: unknown): boolean => {
  const errors = (
    err as { graphQLErrors?: { extensions?: { code?: string } }[] }
  )?.graphQLErrors;
  if (errors?.some((e) => e.extensions?.code === 'RESYNC_REQUIRED'))
    return true;
  return (
    Array.isArray(err) &&
    err.some((e) => e?.extensions?.code === 'RESYNC_REQUIRED')
  );
};

/** App-level owner of the org's single `threadEvents` subscription. Keeps the
 *  sidebar's unread-threads badge live and fans events out to the Inbox page
 *  (via `useThreadEvents`), so opening the inbox doesn't open a second
 *  subscription. No polling: the count is fetched on mount / org switch, on
 *  WS reconnect and RESYNC_REQUIRED, and when an event is ambiguous. */
export const ThreadEventsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const available = useInboxAvailable();
  const orgId = useSelector((s: RootState) => s?.SelectedOrganization?._id);
  const loggedIn = useSelector((s: RootState) => !!s?.Account?._id);
  const active = available && loggedIn && !!orgId;

  const [unread, setUnread] = useState<UnreadState>(EMPTY_UNREAD);
  const unreadRef = useRef(unread);
  const listenersRef = useRef(new Set<ThreadEventsListener>());
  const [generation, setGeneration] = useState(0);

  const commit = useCallback((next: UnreadState) => {
    unreadRef.current = next;
    setUnread(next);
  }, []);

  // ── count snapshot ────────────────────────────────────────────────────
  const fetchIdRef = useRef(0);
  const inFlightRef = useRef(false);
  const dirtyRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  const fetchCount = useCallback(async (): Promise<void> => {
    if (!activeRef.current) return;
    if (inFlightRef.current) {
      dirtyRef.current = true;
      return;
    }
    const id = ++fetchIdRef.current;
    inFlightRef.current = true;
    dirtyRef.current = false;
    try {
      const { data } = await client().query<{ unreadThreadsCount: number }>({
        query: unreadThreadsCountQuery,
        fetchPolicy: 'no-cache',
        context: SILENT_CONTEXT,
      });
      if (id !== fetchIdRef.current) return;
      commit({
        ...unreadRef.current,
        count: Math.max(0, data?.unreadThreadsCount ?? 0),
      });
    } catch (err) {
      handleError(err as Error, {
        category: ErrorCategory.GRAPHQL,
        silent: true,
      });
    } finally {
      if (id === fetchIdRef.current) inFlightRef.current = false;
      // Something moved the count while this was in flight — its result may
      // predate that, so ask again.
      if (id === fetchIdRef.current && dirtyRef.current) void fetchCount();
    }
  }, [commit]);

  const scheduleFetch = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void fetchCount();
    }, REFETCH_DEBOUNCE_MS);
  }, [fetchCount]);

  const step = useCallback(
    ({ state, refetch }: UnreadStep) => {
      if (state !== unreadRef.current) {
        if (inFlightRef.current) dirtyRef.current = true;
        commit(state);
      }
      if (refetch) scheduleFetch();
    },
    [commit, scheduleFetch],
  );

  /** Full resync: forget per-thread knowledge, refetch the count now. */
  const resync = useCallback(() => {
    commit({ ...unreadRef.current, threads: {} });
    fetchIdRef.current += 1;
    inFlightRef.current = false;
    void fetchCount();
    listenersRef.current.forEach((l) => l.onResync());
  }, [commit, fetchCount]);

  // New org (or the feature toggling): fresh state and count. Not on mount —
  // the state starts empty, and children may already have seeded it.
  const scopeRef = useRef<string | null>(null);
  useEffect(() => {
    const scope = `${active}:${orgId ?? ''}`;
    if (scopeRef.current !== null && scopeRef.current !== scope) {
      fetchIdRef.current += 1;
      inFlightRef.current = false;
      commit(EMPTY_UNREAD);
    }
    scopeRef.current = scope;
    if (active) void fetchCount();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [active, orgId, commit, fetchCount]);

  // ── the subscription ──────────────────────────────────────────────────
  const resyncRef = useRef(resync);
  resyncRef.current = resync;
  const stepRef = useRef(step);
  stepRef.current = step;

  useEffect(() => {
    if (!active) return undefined;
    const sub = client()
      .subscribe<{ threadEvents?: ThreadEvent | null }>({
        query: threadEventsSubscription,
      })
      .subscribe({
        next: ({ data }) => {
          const event = data?.threadEvents;
          if (!isUsableEvent(event)) return;
          stepRef.current(applyEventToUnread(unreadRef.current, event));
          listenersRef.current.forEach((l) => l.onEvent(event));
        },
        error: (err: Error) => {
          if (isResyncRequired(err)) {
            resyncRef.current();
            setGeneration((g) => g + 1);
            return;
          }
          handleError(err, { category: ErrorCategory.GRAPHQL, silent: true });
        },
      });
    return () => sub.unsubscribe();
  }, [active, orgId, generation]);

  useWsReconnect(() => {
    if (activeRef.current) resyncRef.current();
  });

  const hub = useMemo<ThreadEventsHub>(
    () => ({
      active,
      addListener: (listener) => {
        listenersRef.current.add(listener);
        return () => {
          listenersRef.current.delete(listener);
        };
      },
      applyReadState: (threadId, unreadCount, seq) =>
        stepRef.current(
          setThreadUnread(unreadRef.current, threadId, unreadCount, seq),
        ),
      seed: (threads) => {
        const next = seedUnread(unreadRef.current, threads);
        if (next !== unreadRef.current) commit(next);
      },
    }),
    [active, commit],
  );

  return (
    <HubContext.Provider value={hub}>
      <UnreadContext.Provider value={active ? unread.count : 0}>
        {children}
      </UnreadContext.Provider>
    </HubContext.Provider>
  );
};

/** Message events need their message; read-state events their thread. */
export const isUsableEvent = (
  event: ThreadEvent | null | undefined,
): event is ThreadEvent =>
  !!event?.threadId && (event.kind === 'READ_STATE_CHANGED' || !!event.message);

/** The shared hub, or null outside the provider (e.g. isolated tests). */
export const useThreadEventsHub = (): ThreadEventsHub | null =>
  useContext(HubContext);

/** Threads with unread messages for my account — the sidebar badge. */
export const useUnreadThreadsCount = (): number => useContext(UnreadContext);

export default ThreadEventsProvider;
